import { useSnackbar } from "notistack";
import React, { createContext, useCallback, useContext, useState } from "react";
import { useSpinner } from "../contexts/SpinnerContext";
import { useUser } from "../contexts/UserContext";
import { extractResultsFromResponse, OptionsData } from "../types";
import { apiFetchWithRetry, REACT_APP_API_ROOT } from "../utilities/api";
import { fetchEndpointData } from "../utilities/dataSourceAdapters";
import { enqueueUniqueSnackbar } from "../utilities/snackbarUtils";
import { useConfig } from "./ConfigContext";

interface OptionsContextType {
  options: OptionsData | null;
  refreshOptions: () => Promise<void>;
  loadOptionsForEndpoint: (endpoints: string | string[]) => Promise<void>;
  forceRefreshEndpoint: (endpoint: string) => Promise<void>;
  isLoading: boolean;
}

// Create the context with a default value matching the interface
export const OptionsContext = createContext<OptionsContextType>({
  options: null,
  refreshOptions: async () => {},
  loadOptionsForEndpoint: async () => {},
  forceRefreshEndpoint: async () => {},
  isLoading: false,
});

export const OptionsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [options, setOptions] = useState<OptionsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingEndpoints, setLoadingEndpoints] = useState<Set<string>>(
    new Set()
  );
  const config = useConfig();
  const { startLoading, stopLoading } = useSpinner();
  const { getStoredToken } = useUser();
  const { enqueueSnackbar } = useSnackbar();

  const loadOptionsForEndpoint = useCallback(
    async (endpoints: string | string[]) => {
      if (!getStoredToken()) {
        return;
      }

      const endpointsArray = Array.isArray(endpoints) ? endpoints : [endpoints];

      const endpointsToLoad = endpointsArray.filter(
        (endpoint) =>
          !loadingEndpoints.has(endpoint) && (!options || !options[endpoint])
      );

      if (endpointsToLoad.length === 0) {
        return;
      }

      startLoading();
      setIsLoading(true);
      setLoadingEndpoints((prev) => {
        const next = new Set(prev);
        endpointsToLoad.forEach((endpoint) => next.add(endpoint));
        return next;
      });

      try {
        const targetEndpoints = config.apiEndpoints.filter((endpoint) =>
          endpointsToLoad.includes(endpoint.tableName)
        );

        const fetchPromises = targetEndpoints.map(async (endpoint) => {
          try {
            let data: any[] = [];
            
            // Use generic adapter function for all data sources
            console.log(`🔄 [OptionsContext] Using adapter system for dropdown data:`, {
              endpointName: endpoint.name,
              dataSourceType: endpoint.dataSourceType,
              fieldFiltering: 'id,name'
            });
            const result = await fetchEndpointData(endpoint, {
              fields: 'id,name'
            });
            
            data = result.data;
            console.log(`✅ [OptionsContext] Loaded ${data.length} records for ${endpoint.name} using ${endpoint.dataSourceType} adapter`);

            const transformedData = data.map((item) => ({
              id: item.id,
              name: item.name,
              description: "", // Not needed for dropdowns, set empty to match interface
              thumbnail: "", // Not needed for dropdowns, set empty to match interface
            }));

            return [endpoint.tableName, transformedData];
          } catch (error) {
            console.warn(
              `Failed to fetch ${endpoint.name} after retries:`,
              error
            );
            enqueueUniqueSnackbar(
              enqueueSnackbar,
              `Failed to load ${endpoint.name}. Please try again later.`,
              { variant: "error" }
            );
            return [endpoint.tableName, []];
          }
        });

        const results = await Promise.all(fetchPromises);
        const newOptionsData = Object.fromEntries(results);

        setOptions((prev) => ({ ...prev, ...newOptionsData }));
      } catch (error) {
        console.error("Error loading options:", error);
        enqueueUniqueSnackbar(
          enqueueSnackbar,
          "Failed to load some options. Please refresh the page.",
          { variant: "error" }
        );
      } finally {
        setLoadingEndpoints((prev) => {
          const next = new Set(prev);
          endpointsToLoad.forEach((endpoint) => next.delete(endpoint));
          return next;
        });
        setIsLoading(false);
        stopLoading();
      }
    },
    [
      config,
      options,
      loadingEndpoints,
      startLoading,
      stopLoading,
      getStoredToken,
      enqueueSnackbar,
    ]
  );

  const forceRefreshEndpoint = useCallback(
    async (endpointTableName: string) => {
      if (!getStoredToken()) {
        return;
      }

      try {
        const targetEndpoint = config.apiEndpoints.find(
          (endpoint) => endpoint.tableName === endpointTableName
        );

        if (!targetEndpoint) {
          console.warn(`Endpoint not found: ${endpointTableName}`);
          return;
        }

        console.log(`[OptionsContext] Force refreshing ${targetEndpoint.name}`);

        const response = await apiFetchWithRetry(
          `${REACT_APP_API_ROOT}${targetEndpoint.api_endpoint}?fields=id,name&page_size=10000`
        );

        const data = extractResultsFromResponse<any>(response);
        const transformedData = data.map((item) => ({
          id: item.id,
          name: item.name,
          description: "", // Not needed for dropdowns
          thumbnail: "", // Not needed for dropdowns
        }));

        // Update just this endpoint's options
        setOptions((prev) => ({
          ...prev,
          [endpointTableName]: transformedData,
        }));

        console.log(`[OptionsContext] Successfully refreshed ${targetEndpoint.name}: ${transformedData.length} items`);
      } catch (error) {
        console.error(`Error force refreshing ${endpointTableName}:`, error);
        enqueueUniqueSnackbar(
          enqueueSnackbar,
          `Failed to refresh ${endpointTableName}. Please try again later.`,
          { variant: "error" }
        );
      }
    },
    [config, getStoredToken, enqueueSnackbar]
  );

  const refreshOptions = useCallback(async () => {
    setOptions(null);
    const previousEndpoints = options ? Object.keys(options) : [];
    if (previousEndpoints.length > 0) {
      try {
        const targetEndpoints = config.apiEndpoints.filter((endpoint) =>
          previousEndpoints.includes(endpoint.tableName)
        );

        // TODO: this page size is limited to 5000 items for options. Its still a bit slow for large amounts of data, like fetching 2000 Places. We need to warn user if there is more data.
        // We Should also allow fetching more options from server somehow.
        const fetchPromises = targetEndpoints.map(async (endpoint) => {
          try {
            let data: any[] = [];
            
            // Use generic adapter function for all data sources
            const result = await fetchEndpointData(endpoint, {
              fields: 'id,name'
            });
            
            data = result.data;
            console.log(`[OptionsContext] Refreshed ${data.length} records for ${endpoint.name} using generic adapter`);

            return [
              endpoint.tableName,
              data.map((item) => ({
                id: item.id,
                name: item.name,
                description: "", // Not needed for dropdowns, set empty to match interface
                thumbnail: "", // Not needed for dropdowns, set empty to match interface
              })),
            ];
          } catch (error) {
            console.warn(
              `Failed to fetch ${endpoint.name} after retries:`,
              error
            );
            enqueueUniqueSnackbar(
              enqueueSnackbar,
              `Failed to refresh ${endpoint.name}. Please try again later.`,
              { variant: "error" }
            );
            return [endpoint.tableName, []];
          }
        });

        const results = await Promise.all(fetchPromises);
        const newOptionsData = Object.fromEntries(results);
        setOptions((prev) => ({ ...prev, ...newOptionsData }));
      } catch (error) {
        console.error("Error refreshing options:", error);
        enqueueUniqueSnackbar(
          enqueueSnackbar,
          "Failed to refresh some options. Please try again later.",
          { variant: "error" }
        );
      }
    }
  }, [config, options, enqueueSnackbar]);

  return (
    <OptionsContext.Provider
      value={{ options, refreshOptions, loadOptionsForEndpoint, forceRefreshEndpoint, isLoading }}
    >
      {children}
    </OptionsContext.Provider>
  );
};

export const useOptions = () => {
  const context = useContext(OptionsContext);
  if (context === undefined) {
    throw new Error("useOptions must be used within an OptionsProvider");
  }
  return context;
};
